// A DEPOSIT ADDRESS IS GIVEN OUT ONLY AFTER THIS BROWSER DERIVES IT (review pack §7: "a deposit QR is shown only after
// re-derivation in the browser"; audit of 21 Oct 2026, E2). The check is lib/custody-keys-local.ts checkAddress — reused,
// never re-derived here. This file only says it:
//
//   derived     Checked — the QR and Copy are offered
//   unchecked   the address is shown to read and Copy stays (today's rule); NO QR — this browser holds no key
//   mismatch    red; neither QR nor Copy — the address must not be given out
import { useState } from 'react';
import { Copy } from 'lucide-react';
import { checkAddress, CHECK_WORDS, type AddressCheck } from '../../../lib/custody-keys-local';
import { buildNetwork } from '../../../lib/ledger-cable';
import { QrSvg } from '../../CosignHandoff';
import { Pill } from '../../kit';
import { StateLine } from '../kit';

/** What this browser derives for a deposit address (account 0's key), against the server's word. */
export function checkOf(a: { address: string; derivation: string | null | undefined }): AddressCheck {
  const network = buildNetwork();
  return network ? checkAddress(network, { address: a.address, derivation: a.derivation, account: 0 }) : 'unchecked';
}

/** The check as a state pill (Astra's "Verified"), the word always beside the colour. */
export function CheckPill({ check }: { check: AddressCheck }) {
  if (check === 'derived') return <Pill tone="good">Checked</Pill>;
  if (check === 'mismatch') return <Pill tone="bad">Mismatch</Pill>;
  return <Pill tone="neutral">Not checked</Pill>;
}

const SHORT: Record<AddressCheck, string> = {
  derived: 'Checked in this browser.',
  unchecked: 'Not checked in this browser — no QR here.',
  mismatch: 'Mismatch — do not give this address out.',
};

/** The one line under an address: the short word, the full sentence behind Details. */
export function CheckLine({ check, className = '' }: { check: AddressCheck; className?: string }) {
  return (
    <StateLine tone={check === 'mismatch' ? 'bad' : check === 'derived' ? 'good' : 'neutral'} className={className} more={CHECK_WORDS[check]}>
      {SHORT[check]}
    </StateLine>
  );
}

export function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* no clipboard here; the address is on screen to read */ }
  }
  return (
    <button type="button" className="btn-secondary inline-flex min-h-11 items-center gap-2 px-4 text-body lg:min-h-10" onClick={() => void copy()}>
      <Copy className="h-4 w-4" aria-hidden />{copied ? 'Copied' : 'Copy address'}
    </button>
  );
}

/** The address to hand a customer: the full address first (to read against the check), then the QR only when the
 *  check passed, then Copy unless it failed. */
export function GiveOut({ address, check, label }: { address: string; check: AddressCheck; label: string }) {
  return (
    <div className="space-y-3">
      <p className="break-all font-mono text-body">{address}</p>
      <CheckLine check={check} />
      {check === 'derived' && (
        <div className="mx-auto w-full max-w-[220px] rounded-xl bg-white p-2">
          <QrSvg text={address} className="h-auto w-full" label={`Deposit address QR code for ${label}`} />
        </div>
      )}
      {check !== 'mismatch' && <div><CopyButton value={address} /></div>}
    </div>
  );
}
